import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, Grid, TextField, MenuItem, IconButton, FormControlLabel, Switch } from '@mui/material';
import { Assessment, PictureAsPdf, GetApp, Save, Delete, Edit } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { onDataUpdated } from '../../utils/dataBus';
import { logAuditEvent } from '../../utils/audit';
import BilanReglementaire from './components/BilanReglementaire';
import { exportBilanOfficialPDF } from './components/ExportBilanPDF';
import * as XLSX from 'xlsx';

interface Ligne { numero_compte: string; debit: number; credit: number }
interface Ecriture { lignes: Ligne[]; date_ecriture: string }
const STORAGE_ECRITURES = 'comptaEBNL_ecritures_local';
const STORAGE_TEMPLATES_PREFIX = 'comptaEBNL_etats_templates_'; // + SN|SMT
const STORAGE_NOTES_PREFIX = 'comptaEBNL_etats_notes_'; // + SN|SMT

type Section = { label: string; prefixes: string[]; side: 'debit' | 'credit' };
interface EtatsTemplate { bilan: Section[]; resultat: Section[] }

// Maquettes par système
const DEFAULT_TEMPLATE_SN: EtatsTemplate = {
  bilan: [
    { label: 'Actifs immobilisés (classe 2)', prefixes: ['2'], side: 'debit' },
    { label: 'Actifs circulants (classe 3)', prefixes: ['3'], side: 'debit' },
    { label: 'Trésorerie actif (classe 5)', prefixes: ['5'], side: 'debit' },
    { label: 'Capitaux propres et assimilés (classe 1)', prefixes: ['1'], side: 'credit' },
    { label: 'Dettes (classe 4)', prefixes: ['4'], side: 'credit' },
  ],
  resultat: [
    { label: 'Produits d’exploitation (classe 70-72)', prefixes: ['70','71','72'], side: 'credit' },
    { label: 'Charges d’exploitation (classe 60-62)', prefixes: ['60','61','62'], side: 'debit' },
    { label: 'Produits financiers (classe 76)', prefixes: ['76'], side: 'credit' },
    { label: 'Charges financières (classe 66)', prefixes: ['66'], side: 'debit' },
    { label: 'Produits exceptionnels (classe 77)', prefixes: ['77'], side: 'credit' },
    { label: 'Charges exceptionnelles (classe 67)', prefixes: ['67'], side: 'debit' },
  ],
};

const DEFAULT_TEMPLATE_SMT: EtatsTemplate = {
  bilan: [
    { label: 'Trésorerie (classe 5)', prefixes: ['5'], side: 'debit' },
    { label: 'Créances/Dettes à court terme (classe 4)', prefixes: ['4'], side: 'credit' },
  ],
  resultat: [
    { label: 'Recettes (classe 7)', prefixes: ['7'], side: 'credit' },
    { label: 'Dépenses (classe 6)', prefixes: ['6'], side: 'debit' },
  ],
};

const loadTemplate = (system: 'SN' | 'SMT'): EtatsTemplate => {
  const key = STORAGE_TEMPLATES_PREFIX + system;
  const raw = localStorage.getItem(key);
  const fallback = system === 'SN' ? DEFAULT_TEMPLATE_SN : DEFAULT_TEMPLATE_SMT;
  if (!raw) return fallback;
  try { return JSON.parse(raw) as EtatsTemplate; } catch { return fallback; }
};

const loadEcritures = (): Ecriture[] => {
  const raw = localStorage.getItem(STORAGE_ECRITURES);
  if (!raw) return [];
  try { return JSON.parse(raw) as Ecriture[]; } catch { return []; }
};

const computeBalance = (ecritures: Ecriture[], fromDate?: string, toDate?: string) => {
  const map: Record<string, { debit: number; credit: number }> = {};
  const inRange = (d: string) => {
    if (fromDate && d < fromDate) return false;
    if (toDate && d > toDate) return false;
    return true;
  };
  for (const e of ecritures) {
    if (!inRange(e.date_ecriture)) continue;
    for (const l of e.lignes) {
      const n = l.numero_compte;
      if (!n) continue;
      if (!map[n]) map[n] = { debit: 0, credit: 0 };
      map[n].debit += Number(l.debit) || 0;
      map[n].credit += Number(l.credit) || 0;
    }
  }
  return Object.entries(map).map(([numero_compte, { debit, credit }]) => ({ numero_compte, debit, credit, solde: debit - credit }));
};

const sumByPrefixes = (rows: { numero_compte: string; debit: number; credit: number; solde: number }[], sections: Section[]) => {
  return sections.map(s => {
    const match = rows.filter(r => s.prefixes.some(p => r.numero_compte.startsWith(p)));
    // For official models: use net balances by normal side
    // debit side (assets/charges): max(debit - credit, 0); credit side (liabilities/products): max(credit - debit, 0)
    const total = match.reduce((acc, r) => {
      const netDebit = r.debit - r.credit;
      const netCredit = r.credit - r.debit;
      const val = s.side === 'debit' ? Math.max(netDebit, 0) : Math.max(netCredit, 0);
      return acc + val;
    }, 0);
    return { label: s.label, total };
  });
};

type Note = { id: string; titre: string; contenu: string };
const loadNotes = (system: 'SN'|'SMT'): Note[] => {
  const raw = localStorage.getItem(STORAGE_NOTES_PREFIX + system);
  if (!raw) return [
    { id: 'n1', titre: 'Principes et méthodes comptables', contenu: 'Décrire les méthodes retenues (amortissements, valorisation, etc.).' },
    { id: 'n2', titre: 'Immobilisations et amortissements', contenu: 'Détail des immobilisations et des plans d’amortissement.' },
    { id: 'n3', titre: 'Dettes et créances', contenu: 'Analyse des échéances et provisions associées.' },
  ];
  try { return JSON.parse(raw) as Note[]; } catch { return []; }
};

const EtatsFinanciers: React.FC = () => {
  const [systemType, setSystemType] = useState<'SN'|'SMT'>('SN');
  const [templates, setTemplates] = useState<EtatsTemplate>(loadTemplate('SN'));
  const [ecritures, setEcritures] = useState<Ecriture[]>(loadEcritures());
  const [notes, setNotes] = useState<Note[]>(loadNotes('SN'));
  const [newNote, setNewNote] = useState<Note>({ id: '', titre: '', contenu: '' });
  const [useRegulatory, setUseRegulatory] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [entityName, setEntityName] = useState<string>('Entité EBNL');
  const [currency, setCurrency] = useState<string>('XOF');
  const [exporting, setExporting] = useState<boolean>(false);

  useEffect(() => {
    const off = onDataUpdated(() => setEcritures(loadEcritures()));
    return off;
  }, []);

  useEffect(() => {
    if (useRegulatory) {
      // fetch regulatory templates and notes from public
      const base = '/etats';
      const tPath = systemType === 'SN' ? `${base}/sn_template.json` : `${base}/smt_template.json`;
      const nPath = systemType === 'SN' ? `${base}/sn_notes.json` : `${base}/smt_notes.json`;
      fetch(tPath).then(r => r.json()).then(setTemplates).catch(() => setTemplates(loadTemplate(systemType)));
      fetch(nPath).then(r => r.json()).then(setNotes).catch(() => setNotes(loadNotes(systemType)));
    } else {
      setTemplates(loadTemplate(systemType));
      setNotes(loadNotes(systemType));
    }
  }, [systemType]);

  useEffect(() => {
    // when toggling regulatory mode, reload
    if (useRegulatory) {
      const base = '/etats';
      const tPath = systemType === 'SN' ? `${base}/sn_template.json` : `${base}/smt_template.json`;
      const nPath = systemType === 'SN' ? `${base}/sn_notes.json` : `${base}/smt_notes.json`;
      fetch(tPath).then(r => r.json()).then(setTemplates).catch(() => setTemplates(loadTemplate(systemType)));
      fetch(nPath).then(r => r.json()).then(setNotes).catch(() => setNotes(loadNotes(systemType)));
    } else {
      setTemplates(loadTemplate(systemType));
      setNotes(loadNotes(systemType));
    }
  }, [useRegulatory]);

  const balanceRows = useMemo(() => computeBalance(ecritures, fromDate || undefined, toDate || undefined), [ecritures, fromDate, toDate]);
  const bilan = useMemo(() => sumByPrefixes(balanceRows, templates.bilan), [balanceRows, templates]);
  const resultat = useMemo(() => sumByPrefixes(balanceRows, templates.resultat), [balanceRows, templates]);

  const handleSaveTemplates = () => {
    localStorage.setItem(STORAGE_TEMPLATES_PREFIX + systemType, JSON.stringify(templates));
    logAuditEvent({ module: 'etats', action: 'config_save', details: { system: systemType } });
  };

  const getExportJSON = () => ({ filename: 'etats_calcules.json', content: JSON.stringify({ system: systemType, bilan, resultat }, null, 2) });
  const getExportCSV = () => {
    const lines: string[] = [];
    lines.push('System,Section,Libelle,Montant');
    bilan.forEach(s => lines.push([systemType, 'Bilan', s.label, s.total.toFixed(2)].join(',')));
    resultat.forEach(s => lines.push([systemType, 'Resultat', s.label, s.total.toFixed(2)].join(',')));
    return { filename: 'etats_calcules.csv', content: lines.join('\n') };
  };

  const printPDF = () => {
    window.print();
  };

  const saveNotes = (items: Note[]) => {
    setNotes(items);
    localStorage.setItem(STORAGE_NOTES_PREFIX + systemType, JSON.stringify(items));
    logAuditEvent({ module: 'etats', action: 'notes_save', details: { system: systemType, count: items.length } });
  };

  const addNote = () => {
    if (!newNote.titre.trim()) return;
    const id = `${Date.now()}`;
    saveNotes([{ ...newNote, id }, ...notes]);
    setNewNote({ id: '', titre: '', contenu: '' });
  };

  const removeNote = (id: string) => {
    saveNotes(notes.filter(n => n.id !== id));
  };

  const onImportNotesJSON = (jsonText: string) => {
    try {
      const arr = JSON.parse(jsonText);
      if (Array.isArray(arr)) saveNotes(arr as Note[]);
    } catch {}
  };

  const getExportNotesJSON = () => ({ filename: `notes_${systemType}.json`, content: JSON.stringify(notes, null, 2) });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          États Financiers SYCEBNL
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Systèmes pris en charge: Système Normal (SN) et Système Minimal de Trésorerie (SMT)
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth select label="Système" value={systemType} onChange={e => setSystemType(e.target.value as 'SN'|'SMT')}>
            <MenuItem value="SN">Système Normal</MenuItem>
            <MenuItem value="SMT">Système Minimal de Trésorerie</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel control={<Switch checked={useRegulatory} onChange={e => setUseRegulatory(e.target.checked)} />} label="Modèle réglementaire (lecture seule)" />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth type="date" label="Du" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField fullWidth type="date" label="Au" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField fullWidth label="Entité" value={entityName} onChange={e => setEntityName(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={1}>
          <TextField fullWidth label="Devise" value={currency} onChange={e => setCurrency(e.target.value)} />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <ImportExportBar getExportCSV={getExportCSV} getExportJSON={getExportJSON} />
        <Button sx={{ ml: 1 }} variant="outlined" startIcon={<PictureAsPdf />} onClick={printPDF}>Imprimer / PDF</Button>
        {useRegulatory && (
          <Button sx={{ ml: 1 }} variant="contained" startIcon={<PictureAsPdf />} disabled={exporting} onClick={async () => {
            try {
              setExporting(true);
              const actifs = templates.bilan.filter(s => s.side === 'debit').map(s => ({ label: s.label, total: bilan.find(b => b.label === s.label)?.total || 0 }));
              const passifs = templates.bilan.filter(s => s.side === 'credit').map(s => ({ label: s.label, total: bilan.find(b => b.label === s.label)?.total || 0 }));
              let templateUrl = systemType === 'SN'
                ? '/etats/pdfs/SN_TEMPLATES_ET_NOTES_PAGE_7.pdf'
                : '/etats/pdfs/SMT_TEMPLATES_ET_NOTES_PAGE_7.pdf';
              // Check availability, fallback to SN if SMT not present
              try {
                const head = await fetch(templateUrl, { method: 'HEAD' });
                if (!head.ok) {
                  templateUrl = '/etats/pdfs/SN_TEMPLATES_ET_NOTES_PAGE_7.pdf';
                }
              } catch {
                templateUrl = '/etats/pdfs/SN_TEMPLATES_ET_NOTES_PAGE_7.pdf';
              }
              await exportBilanOfficialPDF({ templateUrl, entity: entityName, systemLabel: systemType, currency, periodText: `Exercice ${fromDate || '…'} au ${toDate || '…'}`, actifs, passifs, filename: `Bilan_${systemType}.pdf` });
            } catch (e) {
              alert('Export PDF: échec du chargement du modèle ou de la génération. Vérifie que le PDF modèle est accessible.');
            } finally {
              setExporting(false);
            }
          }}>{exporting ? 'Export en cours...' : 'Exporter PDF officiel'}</Button>
        )}
        {useRegulatory && systemType === 'SN' && (
          <Button sx={{ ml: 1 }} variant="outlined" onClick={async () => {
            try {
              const resp = await fetch('/etats/xlsx/CORRESP_BILAN_SN.xlsx');
              const buf = await resp.arrayBuffer();
              const wb = XLSX.read(buf);
              const ws = wb.Sheets[wb.SheetNames[0]];
              const rows = XLSX.utils.sheet_to_json<any>(ws, { defval: '' });
              // rows expected: ordre, label, side, prefixes (separated by ;)
              const bilan = rows
                .filter(r => r.label)
                .sort((a,b) => Number(a.ordre||0) - Number(b.ordre||0))
                .map(r => ({ label: String(r.label), prefixes: String(r.prefixes||'').split(';').map((s:string)=>s.trim()).filter(Boolean), side: (String(r.side||'debit').toLowerCase()==='credit'?'credit':'debit') as 'debit'|'credit' }));
              setTemplates(t => ({ ...t, bilan }));
              alert('Template Bilan SN mis à jour depuis Excel.');
            } catch (e) {
              alert('Import Excel: échec de lecture du mapping SN.');
            }
          }}>Importer Bilan SN (Excel)</Button>
        )}
      </Box>

      <Grid container spacing={2}>
        {useRegulatory && (
          <Grid item xs={12}>
            <BilanReglementaire template={{ bilan: templates.bilan }} balanceRows={balanceRows as any} title={`Bilan réglementaire (${systemType})`} periode={{ from: fromDate || undefined, to: toDate || undefined }} entityName={entityName} systemLabel={systemType} currency={currency} />
          </Grid>
        )}
        {!useRegulatory && (
          <>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Bilan (calculé)</Typography>
                  {bilan.map(b => (
                    <Box key={b.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography>{b.label}</Typography>
                      <Typography sx={{ fontWeight: 600 }}>{b.total.toFixed(2)}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Compte de résultat (calculé)</Typography>
                  {resultat.map(r => (
                    <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography>{r.label}</Typography>
                      <Typography sx={{ fontWeight: 600 }}>{r.total.toFixed(2)}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Références réglementaires</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  href="https://github.com/LnDevAi/ComptaEBNL-IA/blob/main/Knowledge/Ressources/SYCEBNL-GUIDE-D-APPLICATION.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Guide d'application SYCEBNL (PDF)
                </Button>
                <Button
                  variant="outlined"
                  href="https://github.com/LnDevAi/ComptaEBNL-IA/blob/main/Knowledge/Ressources/DECRET-N%C2%B02025-0959-PF-VF.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Décret 2025-0959 (PDF)
                </Button>
                <Button
                  variant="outlined"
                  href="https://github.com/LnDevAi/ComptaEBNL-IA/blob/main/Knowledge/Ressources/PlanComptable_Sycebnl.xlsx"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Plan Comptable SYCEBNL (XLSX)
                </Button>
                <Button
                  variant="outlined"
                  href="https://github.com/LnDevAi/ComptaEBNL-IA/blob/main/Knowledge/Ressources/SN_TEMPLATES_ET_NOTES.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  SN Templates & Notes (PDF)
                </Button>
                <Button
                  variant="outlined"
                  href="https://github.com/LnDevAi/ComptaEBNL-IA/blob/main/Knowledge/Ressources/SMT_TEMPLATES_ET_NOTES.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  SMT Templates & Notes (PDF)
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Paramétrage des templates ({systemType})</Typography>
            <TextField
              fullWidth
              multiline
              minRows={8}
              value={JSON.stringify(templates, null, 2)}
              onChange={e => {
                try { setTemplates(JSON.parse(e.target.value)); } catch {}
              }}
            />
            {!useRegulatory && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="contained" startIcon={<Save />} onClick={handleSaveTemplates}>Enregistrer le template</Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Notes annexes ({systemType})</Typography>
              {!useRegulatory && (
                <ImportExportBar onImportJSON={onImportNotesJSON} getExportJSON={getExportNotesJSON} />
              )}
            </Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Titre" value={newNote.titre} onChange={e => setNewNote({ ...newNote, titre: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField fullWidth multiline minRows={2} label="Contenu" value={newNote.contenu} onChange={e => setNewNote({ ...newNote, contenu: e.target.value })} />
              </Grid>
            </Grid>
            {!useRegulatory && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" onClick={addNote}>Ajouter une note</Button>
              </Box>
            )}
            {notes.length === 0 ? (
              <Typography color="text.secondary">Aucune note pour le moment.</Typography>
            ) : (
              notes.map(n => (
                <Card key={n.id} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 600 }}>{n.titre}</Typography>
                      {!useRegulatory && (
                        <IconButton size="small" onClick={() => removeNote(n.id)}><Delete fontSize="small" /></IconButton>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{n.contenu}</Typography>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EtatsFinanciers;